import { computed, inject, Injectable, signal } from '@angular/core';
import {
  Firestore,
  addDoc,
  collection,
  collectionData,
  doc,
  docData,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  Timestamp,
} from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AuthStore, EmployeeProfile } from '../../../core';
import {
  Onboarding,
  OnboardingDocRequest,
  OnboardingEquipmentItem,
  OnboardingTask,
  OnboardingTemplate,
} from '../models/onboarding.models';
import { Observable, combineLatest, from, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

type ActiveStatus = Extract<Onboarding['status'], 'planned' | 'in_progress' | 'blocked'>;

const ACTIVE_STATUSES: ActiveStatus[] = ['planned', 'in_progress', 'blocked'];
const PAGE_LIMIT = 50;

interface FirestoreUserRecord {
  id: string;
  email?: string | null;
}

type FirestoreEmployeeRecord = Record<string, unknown> & { id: string };
type FirestoreOnboardingRecord = Record<string, unknown> & { id: string };
type FirestoreTemplateRecord = Record<string, unknown> & { id: string };

export interface TeamOnboardingItem {
  employee: EmployeeProfile;
  onboarding: Onboarding | null;
  managerTasks: OnboardingTask[];
  assignedDocRequests: OnboardingDocRequest[];
  overdueTasks: number;
  progressPercent: number;
}

export interface ProgressSnapshot {
  done: number;
  total: number;
  percent: number;
}

export interface TeamOnboardingSummary {
  employee: EmployeeProfile;
  onboarding: Onboarding | null;
  progress: ProgressSnapshot;
}

export interface StartOnboardingInput {
  tenantId: string;
  employeeId: string;
  templateId?: string | null;
}

export interface StartOnboardingResult {
  onboardingId: string;
  onboarding: Onboarding;
  created: boolean;
}

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);
  private readonly auth = inject(Auth);
  private readonly functions = inject(Functions);

  private readonly _loading = signal(false);
  private readonly _myOnboarding = signal<Onboarding | null>(null);
  private readonly _myTasks = signal<OnboardingTask[]>([]);
  private readonly _myDocRequests = signal<OnboardingDocRequest[]>([]);
  private readonly _myEquipment = signal<OnboardingEquipmentItem[]>([]);
  private readonly _teamItems = signal<TeamOnboardingItem[]>([]);

  readonly loading = this._loading.asReadonly();
  readonly myOnboarding = this._myOnboarding.asReadonly();
  readonly myTasks = this._myTasks.asReadonly();
  readonly myDocRequests = this._myDocRequests.asReadonly();
  readonly myEquipment = this._myEquipment.asReadonly();
  readonly teamItems = this._teamItems.asReadonly();

  readonly myProgressPercent = computed(() => {
    const onboarding = this._myOnboarding();
    if (!onboarding) return 0;
    if (onboarding.progressPercent > 0) return onboarding.progressPercent;

    const total =
      this._myTasks().length + this._myDocRequests().length + this._myEquipment().length;
    if (total === 0) return 0;

    const completedTasks = this._myTasks().filter(
      (task) => task.status === 'done' || task.status === 'skipped'
    ).length;
    const completedDocs = this._myDocRequests().filter(
      (docRequest) =>
        docRequest.status === 'approved' ||
        docRequest.status === 'waived' ||
        docRequest.status === 'provided'
    ).length;
    const completedEquipment = this._myEquipment().filter(
      (item) => item.status === 'issued' || item.status === 'returned' || item.status === 'cancelled'
    ).length;

    const completed = completedTasks + completedDocs + completedEquipment;
    return Math.min(100, Math.round((completed / total) * 100));
  });

  readonly outstandingDocRequests = computed(() =>
    this._myDocRequests().filter(
      (docRequest) =>
        docRequest.required && docRequest.status !== 'approved' && docRequest.status !== 'waived'
    )
  );

  getMyOnboarding(tenantId: string, userId: string): Observable<Onboarding | null> {
    const userRef = doc(this.firestore, `tenants/${tenantId}/users/${userId}`);

    return docData(userRef, { idField: 'id' }).pipe(
      switchMap((user) => {
        const record = user as FirestoreUserRecord;
        const email = (record.email ?? '').trim().toLowerCase();
        if (!email) return of<Onboarding | null>(null);

        const employeesRef = collection(this.firestore, `tenants/${tenantId}/employees`);
        const employeeQuery = query(employeesRef, where('email', '==', email), limit(1));

        return collectionData(employeeQuery, { idField: 'id' }).pipe(
          switchMap((employees) => {
            const employee = (employees as FirestoreEmployeeRecord[])[0];
            if (!employee) return of<Onboarding | null>(null);
            return this.getActiveOnboardingForEmployee$(tenantId, employee.id);
          })
        );
      }),
      catchError((error: unknown) => {
        console.error('Failed to stream my onboarding:', error);
        return of<Onboarding | null>(null);
      })
    );
  }

  getTeamOnboardings(tenantId: string, managerId: string): Observable<TeamOnboardingSummary[]> {
    const employeesRef = collection(this.firestore, `tenants/${tenantId}/employees`);
    const reportsQuery = query(
      employeesRef,
      where('managerId', '==', managerId),
      orderBy('lastName'),
      limit(PAGE_LIMIT)
    );

    return collectionData(reportsQuery, { idField: 'id' }).pipe(
      switchMap((reports) => {
        const reportRecords = reports as FirestoreEmployeeRecord[];
        if (reportRecords.length === 0) return of<TeamOnboardingSummary[]>([]);

        const onboardingStreams = reportRecords.map((report) =>
          this.getActiveOnboardingForEmployee$(tenantId, report.id)
        );

        return combineLatest(onboardingStreams).pipe(
          map((onboardings) =>
            reportRecords.map((report, index) => {
              const employee = this.mapEmployee(report.id, report);
              const onboarding = onboardings[index];
              const progress = onboarding
                ? computeProgress(onboarding.taskCounts.completed, onboarding.taskCounts.total)
                : computeProgress(0, 0);

              return {
                employee,
                onboarding,
                progress,
              } satisfies TeamOnboardingSummary;
            })
          )
        );
      }),
      catchError((error: unknown) => {
        console.error('Failed to stream team onboardings:', error);
        return of<TeamOnboardingSummary[]>([]);
      })
    );
  }

  getTemplateList(tenantId: string): Observable<OnboardingTemplate[]> {
    const templatesRef = collection(this.firestore, `tenants/${tenantId}/onboardingTemplates`);
    const templatesQuery = query(templatesRef, orderBy('name'), limit(PAGE_LIMIT));

    const stream$ = new Observable<OnboardingTemplate[]>((subscriber) =>
      onSnapshot(
        templatesQuery,
        (snapshot) => {
          const templates = snapshot.docs.map((docSnap) =>
            this.mapTemplate(docSnap.id, docSnap.data() as Record<string, unknown>)
          );
          subscriber.next(templates);
        },
        (error) => subscriber.error(error)
      )
    );

    return stream$.pipe(
      catchError((error: unknown) => {
        console.error('Failed to stream onboarding templates:', error);
        return of<OnboardingTemplate[]>([]);
      })
    );
  }

  startOnboarding(input: StartOnboardingInput): Observable<StartOnboardingResult> {
    const callable = httpsCallable<StartOnboardingInput, StartOnboardingResult>(
      this.functions,
      'startOnboardingForEmployee'
    );
    return from(this.ensureClaims(input.tenantId)).pipe(
      switchMap(() => from(callable(input))),
      map((result) => result.data)
    );
  }

  async approveProvidedDocRequest(onboardingId: string, requestId: string): Promise<void> {
    const tenantId = this.tenantId();
    const userId = this.authStore.userId();
    if (!tenantId || !userId) return;

    const ref = doc(
      this.firestore,
      `tenants/${tenantId}/onboardings/${onboardingId}/documentRequests/${requestId}`
    );
    await updateDoc(ref, {
      status: 'approved',
      reviewedAt: Timestamp.now(),
      reviewedBy: userId,
      reviewedByName: this.authStore.user()?.displayName ?? null,
    });

    await this.loadTeamOnboarding();
  }

  async completeOnboardingForReport(
    onboardingId: string,
    employee: EmployeeProfile,
    reason?: string | null
  ): Promise<void> {
    const tenantId = this.tenantId();
    const userId = this.authStore.userId();
    if (!tenantId || !userId) return;

    const onboardingRef = doc(this.firestore, `tenants/${tenantId}/onboardings/${onboardingId}`);
    await updateDoc(onboardingRef, {
      status: 'completed',
      completedAt: Timestamp.now(),
      completedBy: userId,
      completedByName: this.authStore.user()?.displayName ?? null,
      completionReason: reason ?? null,
      updatedAt: Timestamp.now(),
    });

    const employeeUserId = await this.resolveUserIdByEmail(tenantId, employee.email);
    if (employeeUserId) {
      const notificationsRef = collection(this.firestore, `tenants/${tenantId}/notifications`);
      const reasonLine = reason?.trim() ? ` Reason: ${reason.trim()}` : '';
      await addDoc(notificationsRef, {
        userId: employeeUserId,
        type: 'system',
        title: 'Onboarding completed',
        body: `Your onboarding has been marked as completed.${reasonLine}`,
        link: '/onboarding/me',
        read: false,
        createdAt: serverTimestamp(),
      });
    }

    await this.loadTeamOnboarding();
  }

  async updateMyTaskStatus(
    taskId: string,
    status: Extract<OnboardingTask['status'], 'done' | 'in_progress'>
  ): Promise<void> {
    const onboarding = this._myOnboarding();
    const tenantId = this.tenantId();
    const userId = this.authStore.userId();
    if (!onboarding || !tenantId || !userId) return;

    const task = this._myTasks().find((item) => item.id === taskId);
    if (!task) return;

    const assigneeUserId = task.assignee.userId ?? null;
    if (assigneeUserId && assigneeUserId !== userId) return;

    const ref = doc(
      this.firestore,
      `tenants/${tenantId}/onboardings/${onboarding.id}/tasks/${taskId}`
    );

    await updateDoc(ref, {
      status,
    });

    await this.loadMyOnboarding();
  }

  async loadMyOnboarding(): Promise<void> {
    const tenantId = this.tenantId();
    if (!tenantId) return;

    this._loading.set(true);
    try {
      const employee = await this.getEmployeeForCurrentUser(tenantId);
      if (!employee) {
        this.resetMyState();
        return;
      }

      const onboarding = await this.getActiveOnboardingForEmployee(tenantId, employee.id);
      if (!onboarding) {
        this.resetMyState();
        return;
      }

      this._myOnboarding.set(onboarding);
      await this.loadOnboardingItems(tenantId, onboarding.id, {
        tasks: this._myTasks,
        docRequests: this._myDocRequests,
        equipment: this._myEquipment,
      });
    } finally {
      this._loading.set(false);
    }
  }

  async loadTeamOnboarding(): Promise<void> {
    const tenantId = this.tenantId();
    const currentUserId = this.authStore.userId();
    if (!tenantId || !currentUserId) return;

    this._loading.set(true);
    try {
      const managerEmployee = await this.getEmployeeForCurrentUser(tenantId);
      if (!managerEmployee) {
        this._teamItems.set([]);
        return;
      }

      const reportsSnap = await getDocs(
        query(
          collection(this.firestore, `tenants/${tenantId}/employees`),
          where('managerId', '==', managerEmployee.id)
        )
      );
      const reports = reportsSnap.docs.map((docSnap) => this.mapEmployee(docSnap.id, docSnap.data()));

      const items = await Promise.all(
        reports.map(async (employee) => {
          const onboarding = await this.getActiveOnboardingForEmployee(tenantId, employee.id);
          if (!onboarding) {
            return {
              employee,
              onboarding: null,
              managerTasks: [],
              assignedDocRequests: [],
              overdueTasks: 0,
              progressPercent: 0,
            } satisfies TeamOnboardingItem;
          }

          const [tasks, docRequests] = await Promise.all([
            this.getTasksForOnboarding(tenantId, onboarding.id),
            this.getDocRequestsForOnboarding(tenantId, onboarding.id),
          ]);
          const managerTasks = tasks.filter(
            (task) => task.assignee.userId === currentUserId || task.assignee.type === 'manager'
          );

          const assignedDocRequests = docRequests.filter(
            (docRequest) =>
              docRequest.assignee.userId === currentUserId || docRequest.assignee.type === 'manager'
          );

          const overdueTasks = managerTasks.filter((task) => this.isOverdue(task.dueDate)).length;
          const progressPercent =
            onboarding.progressPercent > 0
              ? onboarding.progressPercent
              : this.computeProgressFromItems(tasks, docRequests, []);

          return {
            employee,
            onboarding,
            managerTasks,
            assignedDocRequests,
            overdueTasks,
            progressPercent,
          } satisfies TeamOnboardingItem;
        })
      );

      this._teamItems.set(items);
    } finally {
      this._loading.set(false);
    }
  }

  private resetMyState(): void {
    this._myOnboarding.set(null);
    this._myTasks.set([]);
    this._myDocRequests.set([]);
    this._myEquipment.set([]);
  }

  private tenantId(): string | null {
    return this.authStore.tenantId();
  }

  private async getEmployeeForCurrentUser(tenantId: string): Promise<EmployeeProfile | null> {
    const email = (this.authStore.user()?.email ?? '').trim().toLowerCase();
    if (!email) return null;

    const employeesRef = collection(this.firestore, `tenants/${tenantId}/employees`);
    const primarySnap = await getDocs(query(employeesRef, where('email', '==', email)));
    const primary = primarySnap.docs[0];
    if (primary) return this.mapEmployee(primary.id, primary.data());

    const rawEmail = this.authStore.user()?.email ?? '';
    if (rawEmail && rawEmail !== email) {
      const fallbackSnap = await getDocs(query(employeesRef, where('email', '==', rawEmail)));
      const fallback = fallbackSnap.docs[0];
      if (fallback) return this.mapEmployee(fallback.id, fallback.data());
    }

    return null;
  }

  private async getActiveOnboardingForEmployee(tenantId: string, employeeId: string): Promise<Onboarding | null> {
    const onboardingsRef = collection(this.firestore, `tenants/${tenantId}/onboardings`);
    const activeSnap = await getDocs(
      query(
        onboardingsRef,
        where('employeeId', '==', employeeId),
        where('status', 'in', ACTIVE_STATUSES),
        limit(1)
      )
    );
    const activeDoc = activeSnap.docs[0];
    if (activeDoc) return this.mapOnboarding(activeDoc.id, activeDoc.data());
    return null;
  }

  private async resolveUserIdByEmail(tenantId: string, email: string): Promise<string | null> {
    const normalized = (email ?? '').trim().toLowerCase();
    if (!normalized) return null;

    const usersRef = collection(this.firestore, `tenants/${tenantId}/users`);
    const userQuery = query(usersRef, where('email', '==', normalized), limit(1));
    const snap = await getDocs(userQuery);
    if (snap.empty) return null;

    return snap.docs[0].id;
  }

  private getActiveOnboardingForEmployee$(tenantId: string, employeeId: string): Observable<Onboarding | null> {
    const onboardingsRef = collection(this.firestore, `tenants/${tenantId}/onboardings`);
    const activeQuery = query(
      onboardingsRef,
      where('employeeId', '==', employeeId),
      where('status', 'in', ACTIVE_STATUSES),
      limit(1)
    );

    return collectionData(activeQuery, { idField: 'id' }).pipe(
      map((rows) => {
        const record = (rows as FirestoreOnboardingRecord[])[0];
        return record ? this.mapOnboarding(record.id, record) : null;
      }),
      catchError((error: unknown) => {
        console.error('Failed to stream active onboarding:', error);
        return of<Onboarding | null>(null);
      })
    );
  }

  private async loadOnboardingItems(
    tenantId: string,
    onboardingId: string,
    targets: {
      tasks: { set(value: OnboardingTask[]): void };
      docRequests: { set(value: OnboardingDocRequest[]): void };
      equipment: { set(value: OnboardingEquipmentItem[]): void };
    }
  ): Promise<void> {
    const basePath = `tenants/${tenantId}/onboardings/${onboardingId}`;

    const [tasksSnap, docSnap, equipmentSnap] = await Promise.all([
      getDocs(collection(this.firestore, `${basePath}/tasks`)),
      getDocs(collection(this.firestore, `${basePath}/documentRequests`)),
      getDocs(collection(this.firestore, `${basePath}/equipmentItems`)),
    ]);

    targets.tasks.set(tasksSnap.docs.map((docSnap) => this.mapTask(docSnap.id, docSnap.data())));
    targets.docRequests.set(docSnap.docs.map((docSnap) => this.mapDocRequest(docSnap.id, docSnap.data())));
    targets.equipment.set(equipmentSnap.docs.map((docSnap) => this.mapEquipment(docSnap.id, docSnap.data())));
  }

  private async getTasksForOnboarding(tenantId: string, onboardingId: string): Promise<OnboardingTask[]> {
    const snap = await getDocs(
      collection(this.firestore, `tenants/${tenantId}/onboardings/${onboardingId}/tasks`)
    );
    return snap.docs.map((docSnap) => this.mapTask(docSnap.id, docSnap.data()));
  }

  private async getDocRequestsForOnboarding(
    tenantId: string,
    onboardingId: string
  ): Promise<OnboardingDocRequest[]> {
    const snap = await getDocs(
      collection(this.firestore, `tenants/${tenantId}/onboardings/${onboardingId}/documentRequests`)
    );
    return snap.docs.map((docSnap) => this.mapDocRequest(docSnap.id, docSnap.data()));
  }

  private computeProgressFromItems(
    tasks: OnboardingTask[],
    docRequests: OnboardingDocRequest[],
    equipment: OnboardingEquipmentItem[]
  ): number {
    const total = tasks.length + docRequests.length + equipment.length;
    if (total === 0) return 0;

    const completedTasks = tasks.filter(
      (task) => task.status === 'done' || task.status === 'skipped'
    ).length;
    const completedDocs = docRequests.filter(
      (doc) =>
        doc.status === 'approved' ||
        doc.status === 'waived' ||
        doc.status === 'provided'
    ).length;
    const completedEquipment = equipment.filter(
      (item) =>
        item.status === 'issued' ||
        item.status === 'returned' ||
        item.status === 'cancelled'
    ).length;

    const completed = completedTasks + completedDocs + completedEquipment;
    return Math.min(100, Math.round((completed / total) * 100));
  }

  private isOverdue(dueDate?: Timestamp | null): boolean {
    if (!dueDate) return false;
    return dueDate.toDate().getTime() < Date.now();
  }

  private mapTemplate(id: string, data: Record<string, unknown>): OnboardingTemplate {
    const updatedAt = (data['updatedAt'] as Timestamp | undefined) ?? Timestamp.now();
    return {
      id,
      tenantId: data['tenantId'] as string,
      name: (data['name'] as string | undefined) ?? 'Untitled template',
      description: (data['description'] as string | undefined) ?? '',
      status: (data['status'] as OnboardingTemplate['status'] | undefined) ?? 'draft',
      version: (data['version'] as number | undefined) ?? 1,
      tasks: (data['tasks'] as OnboardingTemplate['tasks'] | undefined) ?? [],
      documentRequests:
        (data['documentRequests'] as OnboardingTemplate['documentRequests'] | undefined) ?? [],
      equipmentItems:
        (data['equipmentItems'] as OnboardingTemplate['equipmentItems'] | undefined) ?? [],
      tags: (data['tags'] as string[] | undefined) ?? [],
      createdAt: (data['createdAt'] as Timestamp | undefined) ?? updatedAt,
      createdBy: (data['createdBy'] as string | undefined) ?? '',
      createdByName: (data['createdByName'] as string | undefined) ?? null,
      updatedAt,
      updatedBy: (data['updatedBy'] as string | undefined) ?? '',
      updatedByName: (data['updatedByName'] as string | undefined) ?? null,
      completedAt: (data['completedAt'] as Timestamp | undefined) ?? null,
      completedBy: (data['completedBy'] as string | undefined) ?? null,
      completedByName: (data['completedByName'] as string | undefined) ?? null,
    };
  }

  private mapOnboarding(id: string, data: Record<string, unknown>): Onboarding {
    const updatedAt = (data['updatedAt'] as Timestamp | undefined) ?? Timestamp.now();
    return {
      id,
      tenantId: data['tenantId'] as string,
      templateId: (data['templateId'] as string | null | undefined) ?? null,
      templateVersion: (data['templateVersion'] as number | null | undefined) ?? null,
      employeeId: data['employeeId'] as string,
      userId: (data['userId'] as string | null | undefined) ?? null,
      status: data['status'] as Onboarding['status'],
      startDate: (data['startDate'] as Timestamp | undefined) ?? Timestamp.now(),
      targetCompletionDate: (data['targetCompletionDate'] as Timestamp | undefined) ?? null,
      cancelledAt: (data['cancelledAt'] as Timestamp | undefined) ?? null,
      cancelledBy: (data['cancelledBy'] as string | undefined) ?? null,
      cancelledByName: (data['cancelledByName'] as string | undefined) ?? null,
      notes: (data['notes'] as string | undefined) ?? '',
      progressPercent: (data['progressPercent'] as number | undefined) ?? 0,
      taskCounts: {
        total: (data['taskCounts'] as { total?: number } | undefined)?.total ?? 0,
        completed: (data['taskCounts'] as { completed?: number } | undefined)?.completed ?? 0,
        blocked: (data['taskCounts'] as { blocked?: number } | undefined)?.blocked ?? 0,
        overdue: (data['taskCounts'] as { overdue?: number } | undefined)?.overdue ?? 0,
      },
      createdAt: (data['createdAt'] as Timestamp | undefined) ?? updatedAt,
      createdBy: (data['createdBy'] as string | undefined) ?? '',
      createdByName: (data['createdByName'] as string | undefined) ?? null,
      updatedAt,
      updatedBy: (data['updatedBy'] as string | undefined) ?? '',
      updatedByName: (data['updatedByName'] as string | undefined) ?? null,
      completedAt: (data['completedAt'] as Timestamp | undefined) ?? null,
      completedBy: (data['completedBy'] as string | undefined) ?? null,
      completedByName: (data['completedByName'] as string | undefined) ?? null,
    };
  }

  private mapTask(id: string, data: Record<string, unknown>): OnboardingTask {
    const updatedAt = (data['updatedAt'] as Timestamp | undefined) ?? Timestamp.now();
    const assignee = (data['assignee'] as Record<string, unknown> | undefined) ?? {};
    return {
      id,
      tenantId: data['tenantId'] as string,
      onboardingId: data['onboardingId'] as string,
      templateItemId: (data['templateItemId'] as string | null | undefined) ?? null,
      kind: 'task',
      title: data['title'] as string,
      description: (data['description'] as string | undefined) ?? '',
      order: (data['order'] as number | undefined) ?? 0,
      required: (data['required'] as boolean | undefined) ?? true,
      dueDate: (data['dueDate'] as Timestamp | undefined) ?? null,
      assignee: {
        type: (assignee['type'] as OnboardingTask['assignee']['type'] | undefined) ?? 'employee',
        userId: (assignee['userId'] as string | null | undefined) ?? null,
        employeeId: (assignee['employeeId'] as string | null | undefined) ?? null,
        roleId: (assignee['roleId'] as string | null | undefined) ?? null,
        displayName: (assignee['displayName'] as string | null | undefined) ?? null,
      },
      status: data['status'] as OnboardingTask['status'],
      statusReason: (data['statusReason'] as string | undefined) ?? null,
      lastActorId: (data['lastActorId'] as string | undefined) ?? null,
      lastActorName: (data['lastActorName'] as string | undefined) ?? null,
      lastActedAt: (data['lastActedAt'] as Timestamp | undefined) ?? null,
      createdAt: (data['createdAt'] as Timestamp | undefined) ?? updatedAt,
      createdBy: (data['createdBy'] as string | undefined) ?? '',
      createdByName: (data['createdByName'] as string | undefined) ?? null,
      updatedAt,
      updatedBy: (data['updatedBy'] as string | undefined) ?? '',
      updatedByName: (data['updatedByName'] as string | undefined) ?? null,
      completedAt: (data['completedAt'] as Timestamp | undefined) ?? null,
      completedBy: (data['completedBy'] as string | undefined) ?? null,
      completedByName: (data['completedByName'] as string | undefined) ?? null,
      blocking: (data['blocking'] as boolean | undefined) ?? false,
      checklist:
        (data['checklist'] as Array<{ id: string; label: string; done: boolean }> | undefined) ?? [],
    };
  }

  private mapDocRequest(id: string, data: Record<string, unknown>): OnboardingDocRequest {
    const updatedAt = (data['updatedAt'] as Timestamp | undefined) ?? Timestamp.now();
    const assignee = (data['assignee'] as Record<string, unknown> | undefined) ?? {};
    return {
      id,
      tenantId: data['tenantId'] as string,
      onboardingId: data['onboardingId'] as string,
      templateItemId: (data['templateItemId'] as string | null | undefined) ?? null,
      kind: 'document_request',
      title: data['title'] as string,
      description: (data['description'] as string | undefined) ?? '',
      order: (data['order'] as number | undefined) ?? 0,
      required: (data['required'] as boolean | undefined) ?? true,
      dueDate: (data['dueDate'] as Timestamp | undefined) ?? null,
      assignee: {
        type: (assignee['type'] as OnboardingDocRequest['assignee']['type'] | undefined) ?? 'employee',
        userId: (assignee['userId'] as string | null | undefined) ?? null,
        employeeId: (assignee['employeeId'] as string | null | undefined) ?? null,
        roleId: (assignee['roleId'] as string | null | undefined) ?? null,
        displayName: (assignee['displayName'] as string | null | undefined) ?? null,
      },
      status: data['status'] as OnboardingDocRequest['status'],
      statusReason: (data['statusReason'] as string | undefined) ?? null,
      lastActorId: (data['lastActorId'] as string | undefined) ?? null,
      lastActorName: (data['lastActorName'] as string | undefined) ?? null,
      lastActedAt: (data['lastActedAt'] as Timestamp | undefined) ?? null,
      createdAt: (data['createdAt'] as Timestamp | undefined) ?? updatedAt,
      createdBy: (data['createdBy'] as string | undefined) ?? '',
      createdByName: (data['createdByName'] as string | undefined) ?? null,
      updatedAt,
      updatedBy: (data['updatedBy'] as string | undefined) ?? '',
      updatedByName: (data['updatedByName'] as string | undefined) ?? null,
      completedAt: (data['completedAt'] as Timestamp | undefined) ?? null,
      completedBy: (data['completedBy'] as string | undefined) ?? null,
      completedByName: (data['completedByName'] as string | undefined) ?? null,
      categoryId: (data['categoryId'] as string | undefined) ?? null,
      typeId: (data['typeId'] as string | undefined) ?? null,
      requestedDocumentId: (data['requestedDocumentId'] as string | undefined) ?? null,
      providedDocumentId: (data['providedDocumentId'] as string | undefined) ?? null,
      reviewedAt: (data['reviewedAt'] as Timestamp | undefined) ?? null,
      reviewedBy: (data['reviewedBy'] as string | undefined) ?? null,
      reviewedByName: (data['reviewedByName'] as string | undefined) ?? null,
    };
  }

  private mapEquipment(id: string, data: Record<string, unknown>): OnboardingEquipmentItem {
    const updatedAt = (data['updatedAt'] as Timestamp | undefined) ?? Timestamp.now();
    const assignee = (data['assignee'] as Record<string, unknown> | undefined) ?? {};
    return {
      id,
      tenantId: data['tenantId'] as string,
      onboardingId: data['onboardingId'] as string,
      templateItemId: (data['templateItemId'] as string | null | undefined) ?? null,
      kind: 'equipment',
      title: data['title'] as string,
      description: (data['description'] as string | undefined) ?? '',
      order: (data['order'] as number | undefined) ?? 0,
      required: (data['required'] as boolean | undefined) ?? true,
      dueDate: (data['dueDate'] as Timestamp | undefined) ?? null,
      assignee: {
        type: (assignee['type'] as OnboardingEquipmentItem['assignee']['type'] | undefined) ?? 'employee',
        userId: (assignee['userId'] as string | null | undefined) ?? null,
        employeeId: (assignee['employeeId'] as string | null | undefined) ?? null,
        roleId: (assignee['roleId'] as string | null | undefined) ?? null,
        displayName: (assignee['displayName'] as string | null | undefined) ?? null,
      },
      status: data['status'] as OnboardingEquipmentItem['status'],
      statusReason: (data['statusReason'] as string | undefined) ?? null,
      lastActorId: (data['lastActorId'] as string | undefined) ?? null,
      lastActorName: (data['lastActorName'] as string | undefined) ?? null,
      lastActedAt: (data['lastActedAt'] as Timestamp | undefined) ?? null,
      createdAt: (data['createdAt'] as Timestamp | undefined) ?? updatedAt,
      createdBy: (data['createdBy'] as string | undefined) ?? '',
      createdByName: (data['createdByName'] as string | undefined) ?? null,
      updatedAt,
      updatedBy: (data['updatedBy'] as string | undefined) ?? '',
      updatedByName: (data['updatedByName'] as string | undefined) ?? null,
      completedAt: (data['completedAt'] as Timestamp | undefined) ?? null,
      completedBy: (data['completedBy'] as string | undefined) ?? null,
      completedByName: (data['completedByName'] as string | undefined) ?? null,
      quantity: (data['quantity'] as number | undefined) ?? 1,
      issuedAt: (data['issuedAt'] as Timestamp | undefined) ?? null,
      issuedBy: (data['issuedBy'] as string | undefined) ?? null,
      issuedByName: (data['issuedByName'] as string | undefined) ?? null,
      returnedAt: (data['returnedAt'] as Timestamp | undefined) ?? null,
      returnedBy: (data['returnedBy'] as string | undefined) ?? null,
      returnedByName: (data['returnedByName'] as string | undefined) ?? null,
    };
  }

  private mapEmployee(id: string, data: Record<string, unknown>): EmployeeProfile {
    const toDate = (value: unknown): Date | null => {
      if (!value) return null;
      if (value instanceof Date) return value;
      if (typeof value === 'string') {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      }
      const maybe = value as { toDate?: () => Date };
      return maybe.toDate ? maybe.toDate() : null;
    };

    return {
      id,
      tenantId: this.tenantId() ?? '',
      employeeId: (data['employeeId'] as string | undefined) ?? id,
      firstName: (data['firstName'] as string | undefined) ?? '',
      lastName: (data['lastName'] as string | undefined) ?? '',
      email: (data['email'] as string | undefined) ?? '',
      phone: (data['phone'] as string | undefined) ?? '',
      avatar: (data['avatar'] as string | null | undefined) ?? null,
      personalDetails: data['personalDetails'] as EmployeeProfile['personalDetails'],
      departmentId: (data['departmentId'] as string | undefined) ?? '',
      teamId: (data['teamId'] as string | null | undefined) ?? null,
      managerId: (data['managerId'] as string | null | undefined) ?? null,
      locationId: (data['locationId'] as string | undefined) ?? '',
      jobTitle: (data['jobTitle'] as string | undefined) ?? '',
      contractType:
        (data['contractType'] as EmployeeProfile['contractType'] | undefined) ?? 'full_time',
      startDate: toDate(data['startDate']) ?? new Date(),
      endDate: toDate(data['endDate']),
      workingHoursPerWeek: (data['workingHoursPerWeek'] as number | undefined) ?? 40,
      compensation:
        (data['compensation'] as EmployeeProfile['compensation'] | undefined) ?? {
          salaryType: 'monthly',
          baseRate: 0,
          currency: this.authStore.tenant()?.settings?.baseCurrency ?? 'USD',
        },
      addresses: data['addresses'] as EmployeeProfile['addresses'],
      bankingDetails: data['bankingDetails'] as EmployeeProfile['bankingDetails'],
      emergencyContacts: data['emergencyContacts'] as EmployeeProfile['emergencyContacts'],
      education: data['education'] as EmployeeProfile['education'],
      workPermits: data['workPermits'] as EmployeeProfile['workPermits'],
      profileCompleteness: (data['profileCompleteness'] as number | undefined) ?? 0,
      createdAt: toDate(data['createdAt']) ?? new Date(),
      updatedAt: toDate(data['updatedAt']) ?? new Date(),
    };
  }

  private async ensureClaims(tenantId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    const tokenResult = await user.getIdTokenResult();
    const claimTenantId = tokenResult.claims['tenantId'];
    const claimRole = tokenResult.claims['role'];
    const hasTenantMatch = claimTenantId === tenantId;
    const hasRole = typeof claimRole === 'string' && claimRole.length > 0;

    if (hasTenantMatch && hasRole) {
      return;
    }

    const syncClaims = httpsCallable<{ tenantId: string }, { tenantId: string; role: string }>(
      this.functions,
      'syncUserClaims'
    );
    await syncClaims({ tenantId });
    await user.getIdToken(true);
  }
}

export function computeProgress(done: number, total: number): ProgressSnapshot {
  const safeTotal = total > 0 ? total : 0;
  const safeDone = safeTotal === 0 ? 0 : Math.min(Math.max(done, 0), safeTotal);
  const percent = safeTotal === 0 ? 0 : Math.round((safeDone / safeTotal) * 100);
  return {
    done: safeDone,
    total: safeTotal,
    percent,
  };
}
